import React from "react";
import { ShieldCheck, ArrowRight, Phone, MessageSquare, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";

interface Login2FAFormProps {
  otp: string;
  helperText: string;
  loading: boolean;
  verifyMode?: string;
  onVerifyModeChange?: (mode: string) => void;
  onOTPChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handle2FASubmit: (e: React.FormEvent) => void;
  cancel2FA: (e: React.FormEvent) => void;
}

const Login2FAForm: React.FC<Login2FAFormProps> = ({
  otp,
  helperText,
  loading,
  verifyMode = "mobile",
  onVerifyModeChange,
  onOTPChange,
  handle2FASubmit,
  cancel2FA,
}) => (
  <motion.form
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="w-full space-y-5"
    onSubmit={handle2FASubmit}
  >
    {/* Verification Channel Selector if callback provided */}
    {onVerifyModeChange && (
      <div className="p-1 bg-slate-100/80 rounded-2xl flex items-center gap-1 border border-slate-200/60">
        <button
          type="button"
          onClick={() => onVerifyModeChange("mobile")}
          className={`flex-1 py-2 px-3 rounded-xl font-bold text-xs transition-all duration-200 flex items-center justify-center gap-1.5 ${
            verifyMode === "mobile"
              ? "bg-white text-slate-900 shadow-md shadow-slate-900/5"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          <Phone size={14} className={verifyMode === "mobile" ? "text-primary" : "text-slate-400"} />
          <span>SMS / Mobile</span>
        </button>

        <button
          type="button"
          onClick={() => onVerifyModeChange("whatsapp")}
          className={`flex-1 py-2 px-3 rounded-xl font-bold text-xs transition-all duration-200 flex items-center justify-center gap-1.5 ${
            verifyMode === "whatsapp"
              ? "bg-white text-emerald-900 shadow-md shadow-emerald-900/5"
              : "text-slate-500 hover:text-emerald-700"
          }`}
        >
          <MessageSquare size={14} className={verifyMode === "whatsapp" ? "text-emerald-600" : "text-slate-400"} />
          <span>WhatsApp OTP</span>
        </button>
      </div>
    )}

    <div className="space-y-2">
      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">
        Verification Code (OTP)
      </label>
      <div className="relative group">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">
          <ShieldCheck size={18} />
        </div>
        <input
          type="text"
          maxLength={6}
          autoComplete="off"
          value={otp}
          onChange={onOTPChange}
          placeholder="Enter 6-digit code"
          className={`w-full pl-12 pr-4 py-3.5 bg-slate-50 border-2 rounded-2xl outline-none text-center font-mono tracking-[0.3em] text-lg font-bold transition-all duration-200 ${
            helperText
              ? "border-rose-100 bg-rose-50/30 focus:border-rose-400"
              : "border-transparent focus:border-primary focus:bg-white focus:shadow-lg focus:shadow-primary/5"
          }`}
        />
      </div>
      {helperText && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs text-rose-500 mt-2 flex items-center gap-1.5 font-medium ml-1"
        >
          <AlertCircle size={14} /> {helperText}
        </motion.p>
      )}
    </div>

    <div className="pt-2 space-y-3">
      <button
        type="submit"
        disabled={loading || !otp.trim()}
        className="w-full group relative py-4 px-6 bg-primary text-white font-bold rounded-2xl shadow-xl shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
        {loading ? (
          <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            <span>Submit Code</span>
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </>
        )}
      </button>

      <button
        type="button"
        onClick={(e) => cancel2FA(e)}
        className="w-full py-2.5 text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors text-center"
      >
        Cancel & Return to Login
      </button>
    </div>
  </motion.form>
);

export default Login2FAForm;
